import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api, { formatApiError } from "../../lib/api";

const CATEGORIES = ["Cleaning Tips", "Home Maintenance", "Commercial Care", "Lagos Living"];
const COVERS = ["/assets/hero.jpg", "/assets/living.jpg", "/assets/kitchen.jpg", "/assets/office.jpg", "/assets/spray.jpg", "/assets/wiping.jpg", "/assets/clean-desk.jpg", "/assets/hallway.jpg"];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    ["link", "image"],
    [{ align: [] }],
    ["clean"],
  ],
};

export default function AdminPostEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", content: "", category: CATEGORIES[0], cover_image: COVERS[0], status: "draft" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/posts/${id}`).then((r) => {
      const { title, slug, excerpt, content, category, cover_image, status } = r.data;
      setForm({ title, slug, excerpt, content, category, cover_image: cover_image || COVERS[0], status });
    }).catch((e) => { toast.error(formatApiError(e)); navigate("/admin/posts"); })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const quill = useMemo(() => QUILL_MODULES, []);

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Featured image must be 5MB or less"); return; }
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/uploads/blog", body, { headers: { "Content-Type": "multipart/form-data" } });
      set("cover_image", data.url);
      toast.success("Featured image uploaded");
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };

  const save = async (status) => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, status };
      if (isEdit) {
        await api.put(`/admin/posts/${id}`, payload);
        toast.success(status === "published" ? "Post published!" : "Draft saved");
      } else {
        await api.post("/admin/posts", payload);
        toast.success(status === "published" ? "Post published!" : "Draft saved");
      }
      navigate("/admin/posts");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-brand-emerald border-t-brand-lime rounded-full animate-spin" /></div>;

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#D6E4DC] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50";
  const labelCls = "block text-xs font-bold text-brand-emerald uppercase tracking-wider mb-1.5";

  return (
    <div data-testid="admin-post-editor">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/posts" data-testid="editor-back-btn" className="p-2 rounded-lg bg-white border border-[#D6E4DC] text-brand-emerald hover:bg-brand-mint"><ArrowLeft className="w-4 h-4" /></Link>
          <h1 className="font-display text-2xl font-extrabold text-brand-emerald">{isEdit ? "Edit Post" : "New Post"}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => save("draft")} disabled={saving} data-testid="editor-save-draft-btn" className="px-5 py-2.5 rounded-full bg-white border border-[#D6E4DC] text-brand-emerald text-sm font-bold hover:border-brand-emerald disabled:opacity-50 transition-colors">Save Draft</button>
          <button onClick={() => save("published")} disabled={saving} data-testid="editor-publish-btn" className="px-5 py-2.5 rounded-full bg-brand-emerald text-white text-sm font-bold hover:bg-brand-lime disabled:opacity-50 transition-colors flex items-center gap-2">
            <Save className="w-4 h-4" /> Publish
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-[#D6E4DC] p-6 space-y-4">
            <div>
              <label className={labelCls}>Title *</label>
              <input data-testid="editor-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. 5 Ways to Keep Your Kitchen Spotless" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>URL Slug (optional — auto-generated)</label>
              <input data-testid="editor-slug-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="5-ways-to-keep-your-kitchen-spotless" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Excerpt</label>
              <textarea data-testid="editor-excerpt-input" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} placeholder="Short summary shown on blog cards…" className={inputCls} />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#D6E4DC] p-6">
            <label className={labelCls}>Content * (rich text — headings, images, links, colours)</label>
            <div data-testid="editor-content-quill">
              <ReactQuill theme="snow" value={form.content} onChange={(v) => set("content", v)} modules={quill} placeholder="Write something properly good…" />
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-[#D6E4DC] p-6">
            <label className={labelCls}>Category</label>
            <select data-testid="editor-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="bg-white rounded-2xl border border-[#D6E4DC] p-6">
            <label className={labelCls}>Cover Image</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {COVERS.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`editor-cover-${c.split("/").pop().split(".")[0]}`}
                  onClick={() => set("cover_image", c)}
                  className={`rounded-xl overflow-hidden border-2 transition-all ${form.cover_image === c ? "border-brand-lime ring-2 ring-brand-lime/30" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <img src={c} alt={c} className="w-full h-16 object-cover" />
                </button>
              ))}
            </div>
            <label className="mt-3 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-brand-lime/50 text-brand-emerald font-bold text-sm cursor-pointer hover:bg-brand-mint transition-colors">
              <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Featured Image"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" disabled={uploading} onChange={(e) => uploadCover(e.target.files?.[0])} />
            </label>
            {form.cover_image && <img src={form.cover_image} alt="Featured preview" className="mt-3 w-full h-36 object-cover rounded-xl border border-[#D6E4DC]" />}
            <input data-testid="editor-cover-url-input" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="…or paste an image URL" className={`${inputCls} mt-3`} />
          </div>
          <div className="bg-brand-mint rounded-2xl border border-[#D6E4DC] p-5 text-xs text-brand-muted leading-relaxed">
            Drafts are hidden from the public blog. Published posts appear immediately and accept moderated comments.
          </div>
        </div>
      </div>
    </div>
  );
}
